import { useContext, useMemo } from 'react';

import { GravityFormsDispatchContext, GravityFormsStateContext } from './GravityFormsProvider';

export const useGravityFormsDispatch = () =>
  useContext(GravityFormsDispatchContext);

export const useGravityFormsState = () =>
  useContext(GravityFormsStateContext);

export const useSelectCaptchaFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.captchaFields[id],
    [id, state.fields.captchaFields]);
};

export const useSelectCheckboxFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.checkboxFields[id],
    [id, state.fields.checkboxFields]);
};

export const useSelectEmailFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.emailFields[id],
    [id, state.fields.emailFields]);
};

export const useSelectHiddenFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.hiddenFields[id],
    [id, state.fields.hiddenFields]);
};

export const useSelectNumberFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.numberFields[id],
    [id, state.fields.numberFields]);
};

export const useSelectMultiSelectFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.multiSelectFields[id],
    [id, state.fields.multiSelectFields]);
};

export const useSelectPhoneFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.phoneFields[id],
    [id, state.fields.phoneFields]);
};

export const useSelectRadioFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.radioFields[id],
    [id, state.fields.radioFields]);
};

export const useSelectSelectFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.selectFields[id],
    [id, state.fields.selectFields]);
};

export const useSelectTextAreaFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.textAreaFields[id],
    [id, state.fields.textAreaFields]);
};

export const useSelectTextFieldValue = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.fields.textFields[id],
    [id, state.fields.textFields]);
};

export const useSelectInitialized = () => {
  const state = useGravityFormsState();

  return useMemo(() => state.initialized, [state.initialized]);
};

export type GravityFormValue =
  | (null | string | undefined)[]
  | null
  | string
  | string[]
  | undefined;

export const useGetFieldValues = (ids: number[]): Record<number, GravityFormValue> => {
  const state = useGravityFormsState();

  // We might reconsider how we store values in state.
  // This is a bit of a mess.
  return ids.reduce<Record<number, GravityFormValue>>((prev, id) => {
    const checkboxField = state.fields.checkboxFields[id];

    if (checkboxField) {
      prev[id] = checkboxField.checkboxValues.map(cv => cv.value);

      return prev;
    }

    const emailField = state.fields.emailFields[id];

    if (emailField) {
      prev[id] = emailField.emailValues.value;

      return prev;
    }

    const hiddenField = state.fields.hiddenFields[id];

    if (hiddenField) {
      prev[id] = hiddenField.value;

      return prev;
    }

    const multiSelectField = state.fields.multiSelectFields[id];

    if (multiSelectField) {
      prev[id] = multiSelectField.values;

      return prev;
    }

    const phoneField = state.fields.phoneFields[id];

    if (phoneField) {
      prev[id] = phoneField.value;

      return prev;
    }

    const radioField = state.fields.radioFields[id];

    if (radioField) {
      prev[id] = radioField.value;

      return prev;
    }

    const selectField = state.fields.selectFields[id];

    if (selectField) {
      prev[id] = selectField.value;

      return prev;
    }

    const textAreaField = state.fields.textAreaFields[id];

    if (textAreaField) {
      prev[id] = textAreaField.value;

      return prev;
    }

    const textField = state.fields.textFields[id];

    if (textField) {
      prev[id] = textField.value;

      return prev;
    }

    const addressField = state.fields.addressFields[id];

    if (addressField) {
      prev[id] = [
        addressField.addressValues.street,
        addressField.addressValues.lineTwo,
        addressField.addressValues.city,
        addressField.addressValues.state,
        addressField.addressValues.zip,
        addressField.addressValues.country,
      ];

      return prev;
    }

    const dateField = state.fields.dateFields[id];

    if (dateField) {
      prev[id] = dateField.value;

      return prev;
    }

    const websiteField = state.fields.websiteFields[id];

    if (websiteField) {
      prev[id] = websiteField.value;

      return prev;
    }

    const timeField = state.fields.timeFields[id];

    if (timeField) {
      prev[id] = timeField.value;

      return prev;
    }

    const numberField = state.fields.numberFields[id];

    if (numberField) {
      prev[id] = numberField.value;

      return prev;
    }

    return prev;
  }, {});
};

export const useGetFormErrors = () => {
  const state = useGravityFormsState();

  return useMemo(() => state.errors, [state.errors]);
};

export const useGetFieldErrors = (id: number) => {
  const state = useGravityFormsState();

  return useMemo(() => state.errors[id] ?? [], [id, state.errors]);
};
